/*
MIT License

Copyright (c) 2023 Artem Churyukin

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.
*/

import psl_templates from './psl_templates.js';

const _templates=new Map();

const _render_container=document.createElement('div');
const _render_container_table=document.createElement('tbody');

function _add_helper(name,func){
	psl_templates.helpers.add(name,func);
}

async function _add_template(name,template){
	template=psl_templates.templates.add(name,template);
	_templates.set(name,template);
	const res=_render_template.bind(this, template);
	res.template_name=name;
	return res;
}

const _uid=new Uint32Array(2);

function _render_template(name,data={},rpoints_configs){
	window.crypto.getRandomValues(_uid);
	data.psl_ui_id='psl_ui_id_'+_uid[0]+'_'+_uid[1];
	const str_html=name.constructor===Function?name(data):_templates.get(name)(data);
	let container;
	if(str_html.substr(0,3)==='<tr'){
		container=_render_container_table;
	} else {
		container=_render_container;
	}
	container.innerHTML=str_html;
	let rpoints=_get_rpoints(container,rpoints_configs);
	let els=Array.from(container.childNodes);
	for(let i=els.length;i--;) if(els[i].nodeName==='#text') els.splice(i,1);
	delete data.psl_ui_id;
	let res={els,rpoints,name:name.template_name||name};
	if(els.length===1) res.el=els[0];
	return res;
}

function _resolve_path(rpoints,rpoint,el){
	rpoint=rpoint.split('.');
	if(rpoint.length===1) return rpoints[rpoint[0]]=el;
	let target=rpoints;
	for(let i=0;i<rpoint.length-1;i++){
		if(!target[rpoint[i]]) target[rpoint[i]]={};
		target=target[rpoint[i]];
	}
	target[rpoint[rpoint.length-1]]=el;
}

function _insert_template(config){
	//config.template - _render_template data,
	//config.target - {view,before/after};
	let template=_render_template(...config.template);
	for(let [name,node] of Object.entries(template.rpoints)) config.target.view.rpoints[name]=node;
	config.target.parent=(config.target.after?config.target.after.parentNode:undefined)||(config.target.before?config.target.before.parentNode:undefined);
	if(config.target.after) config.target.before=config.target.after.nextElementSibling;
	template.els.forEach(el=>{
		if(config.target.before){
			config.target.parent.insertBefore(el,config.target.before);
		} else {
			config.target.parent.appendChild(el);
		}
	});
}

function _get_rpoints(node,configs={}){
	let els=Array.from(node.querySelectorAll('[data-rpoint]'));
	let rpoints={};
	for(let el of els){
		let rpoint=el.dataset.rpoint;
		if(el.dataset.psl_ui&&_classes[el.dataset.psl_ui]) el=new _classes[el.dataset.psl_ui](el,configs[rpoint]);
		_resolve_path(rpoints,rpoint,el);
		delete el.dataset.rpoint;
		delete el.dataset.psl_ui;
	}
	return rpoints;
}

const _classes={};

const _DOM=(function(){

	function _parent(el,filter=()=>true){
		while(el&&!filter(el)) el=el.parentNode;
		return el;
	}
	
	function _controller(el,name='controller'){
		while(el&&!el[name]) el=el.parentNode;
		return el[name];
	}

	function _children(el,filter=()=>true){
		return Array.from(el.childNodes).filter(filter);
	}

	function _text(el,text){
// 			el.innerHTML=text;
		if(el.psl_ui_dom_text) return el.psl_ui_dom_text.textContent=text;
		el.psl_ui_dom_text=_children(el,el=>el.nodeName==='#text')[0];
		if(!el.psl_ui_dom_text){
			el.psl_ui_dom_text=document.createTextNode('');
			el.appendChild(el.psl_ui_dom_text);
		}
		el.psl_ui_dom_text.textContent=text;
	}

	function _replace(source,target){
		source.parentNode.insertBefore(target,source);
		source.parentNode.removeChild(source);
	}
	
	function _clear(el){
		while(el.firstChild) el.removeChild(el.firstChild);
	}
	
	const _render_container=document.createElement('div');
	function _str2html(str){
		_render_container.innerHTML=str;
		let els=Array.from(_render_container.childNodes);
		for(let i=els.length;i--;) if(els[i].nodeName==='#text') els.splice(i,1);
		if(els.length===1) return els[0];
		return els;
	}
	function _str2text(str){
		_render_container.innerHTML=str;
		return _render_container.innerText.replace(/\s\s/g,' ').replace(/\>/g,'&gt;').replace(/\</g,'&lt;');
	}
	
	function _content(el,content){
		_clear(el);
		
		if(content.constructor===String){
			el.innerHTML=content;
		} else {
			_clear(el);
			
			let arr;
			if(content.constructor===Array){
				arr=content;
			} else if(content instanceof HTMLElement){
				arr=[content];
			} else if(content.els){
				arr=content.els;
			} else if(content.view){
				arr=[content.view];
			}
			arr.forEach(arr_el=>{
				if(arr_el instanceof HTMLElement){
					el.appendChild(arr_el);
				} else if(arr_el.constructor===String){
					const rendered=_str2html(arr_el);
					if(rendered.constructor===Array){
						rendered.forEach(arr_el=>el.appendChild(arr_el));
					} else {
						el.appendChild(rendered);
					}
				} else {
					console.error('Unknown type',arr_el,el);
					debugger;
				}
			});
		}
	}

	const _ext={
		parent:_parent,
		controller:_controller,
		children:_children,
		text:_text,
		replace:_replace,
		clear:_clear,
		str2html:_str2html,
		str2text:_str2text,
		content:_content,
	}

	return _ext;
})();

export default {
	helpers:{
		add:_add_helper
	},
	templates:{
		add:_add_template,
		render:_render_template,
		rpoints:_get_rpoints,
		insert:_insert_template,
	},
	classes:_classes,
	DOM:_DOM
}
